import type { ClarificationItemShape, ItemState } from "./types";
import { FreeTextInput } from "./FreeTextInput";
import { FilterInput } from "./FilterInput";
import { DropdownInput } from "./DropdownInput";
import { ChipsInput } from "./ChipsInput";
import { RadioListInput } from "./RadioListInput";

interface ClarificationQuestionProps {
    item: ClarificationItemShape;
    idx: number;
    state: ItemState;
    onChange: (state: ItemState) => void;
    /** Rendered above the input when the agent sent a widget preview with the question */
    previewCard?: React.ReactNode;
    /** Rendered below the title/message */
    messageBody: React.ReactNode;
}

function resolveVariant(item: ClarificationItemShape): "freetext" | "filter" | "multi-dropdown" | "single-dropdown" | "chips" | "list" {
    if (item.freeText && item.options.length === 0) return "freetext";
    if (item.filterPayload) return "filter";
    if (item.multi) return "multi-dropdown";
    if (item.uiType === "select" || item.options.length > 10) return "single-dropdown";
    if (item.uiType === "chips" && !item.options.some((o) => o.split(" ").length > 3)) return "chips";
    return "list";
}

export function ClarificationQuestion({ item, idx, state, onChange, previewCard, messageBody }: ClarificationQuestionProps) {
    const radioName = `clarification-${idx}`;
    const variant = resolveVariant(item);

    const handleSelections = (selections: string[], customText?: string) => {
        onChange({ ...state, selections, customText: customText ?? state.customText });
    };

    const handleCustomText = (customText: string) => {
        onChange({ ...state, customText });
    };

    return (
        <div className="space-y-1.5">
            <p className="text-xs font-semibold text-[#2d3a3f]">{item.title}</p>
            {messageBody}
            {previewCard}

            {variant === "freetext" && (
                <FreeTextInput
                    value={state.selections[0] ?? ""}
                    onChange={(v) => onChange({ ...state, selections: [v] })}
                />
            )}

            {variant === "filter" && item.filterPayload && (
                <FilterInput
                    drafts={state.filterDrafts}
                    fieldOptions={item.filterPayload.fieldOptions}
                    isOpen={state.filterOpen}
                    onToggleOpen={() => onChange({ ...state, filterOpen: !state.filterOpen })}
                    onChange={(drafts) => onChange({ ...state, filterDrafts: drafts })}
                />
            )}

            {variant === "multi-dropdown" && (
                <DropdownInput
                    options={item.options}
                    selected={state.selections}
                    multi
                    title={item.title}
                    onChange={(selections) => handleSelections(selections)}
                />
            )}

            {variant === "single-dropdown" && (
                <DropdownInput
                    options={item.options}
                    selected={state.selections}
                    multi={false}
                    title={item.title}
                    onChange={(selections) => handleSelections(selections)}
                />
            )}

            {variant === "chips" && (
                <ChipsInput
                    options={item.options}
                    selected={state.selections}
                    customText={state.customText}
                    radioName={radioName}
                    onChange={handleSelections}
                />
            )}

            {variant === "list" && (
                <RadioListInput
                    options={item.options}
                    selected={state.selections}
                    customText={state.customText}
                    radioName={radioName}
                    onChange={handleSelections}
                />
            )}
        </div>
    );
}
